import _ from "lodash";
import { RECTANGLE_ELEMENT_CONFIG } from "../KonvaConfig";
import { KONVA_ATTR, KONVA_EVENTS } from "../KonvaConstants";
import {
  generateElementName,
  getCanvasRelativeSize,
  listenBackEvents,
  listenDocumentEvent,
} from "../../KonvaCommonUtils";
import { generateRandomId } from "Utils";

/**
 *
 * @param {*} item
 * @param {*} konva
 * @returns New shape instance
 */
export const createRectangleNode = (
  item,
  { mode, questionType, layer, transformer, konva } = {}
) => {
  const strokeWidth = getCanvasRelativeSize(
    RECTANGLE_ELEMENT_CONFIG.STROKE_WIDTH
  );
  const props = {
    id: _.get(item, "id"),
    x: _.get(item, "x"),
    y: _.get(item, "y"),
    name: generateElementName(item),
    draggable: _.get(item, "draggable", false),
    dash: [strokeWidth, strokeWidth],
    strokeWidth: strokeWidth,
    stroke: RECTANGLE_ELEMENT_CONFIG.STROKE_COLOR,
    width: _.get(item, "width", RECTANGLE_ELEMENT_CONFIG.DEFAULT_SIZE),
    height: _.get(item, "height", RECTANGLE_ELEMENT_CONFIG.DEFAULT_SIZE),
  };

  if (mode === "create") {
    props.fillEnabled = false;
    props.hitStrokeWidth = Math.min(strokeWidth * 9, 20);
  }

  const dash = {
    ...props,
    name: props.id + "-dash-support",
    id: props.id + "-dash-support",
    stroke: "white",
    listening: false,
    draggable: false,
  };
  delete dash.dash;
  delete dash.hitFunc;

  const supportDashLine = new konva.Rect(dash);
  layer.add(supportDashLine);

  const node = new konva.Rect(props);
  layer.add(node);

  const updateSupportDashLine = () => {
    supportDashLine.setAttrs(node.attrs);
    supportDashLine.dash([10, 0]);
    supportDashLine.stroke("white");
  };

  if (transformer) {
    node.on(KONVA_EVENTS.DRAGMOVE, updateSupportDashLine);
    transformer.on(KONVA_EVENTS.TRANSFORM, updateSupportDashLine);
  }

  if (questionType?.isDragAndDrop) {
    // * adding correct element as attributes
    node.setAttr(
      KONVA_ATTR.DROPZONE_TAGGED_ANSWER,
      _.get(item, "correctElements", [])
    );
  }

  return node;
};

/**
 *
 * @param {*} props
 * @description draw rectangle
 */
export const drawRectangleEventListener = props => {
  const { konva, stage, layer, transformer } = props;
  stage.container().style.cursor = "crosshair";

  let rectangle, startPos, isShiftPressed;

  /**
   * cancelling drawing mode and event
   */
  const cancelDrawing = e => {
    if (!stage.container().contains(e.target)) {
      stage.setAttr(KONVA_ATTR.IS_DRAWING_POLYGON, false);
      stage.container().style.cursor = "default";

      if (_.isFunction(props?.onDrawCancel)) props.onDrawCancel();
      if (_.isFunction(cleanClickEvent)) cleanClickEvent();
      if (_.isFunction(cleanKeyUpEvent)) cleanKeyUpEvent();
      if (_.isFunction(cleanKeyDownEvent)) cleanKeyDownEvent();
    }
  };

  const listenForShift = e => {
    isShiftPressed = _.get(e, "key") === "Shift" ? true : isShiftPressed;
  };
  const unlistenFoShift = e => {
    isShiftPressed = _.get(e, "key") === "Shift" ? false : isShiftPressed;
  };

  const cleanClickEvent = listenDocumentEvent("click", cancelDrawing);
  const cleanKeyUpEvent = listenDocumentEvent("keyup", unlistenFoShift);
  const cleanKeyDownEvent = listenDocumentEvent("keydown", listenForShift);

  /**
   * starting drawing rectangle on mouse down
   */
  const onMouseDown = () => {
    const pos = stage.getPointerPosition();
    startPos = pos;

    rectangle = new konva.Rect({
      id: generateRandomId(),
      x: pos.x,
      y: pos.y,
      width: 0,
      height: 0,
      stroke: RECTANGLE_ELEMENT_CONFIG.DRAW_MODE_STROKE_COLOR,
      strokeWidth: getCanvasRelativeSize(
        RECTANGLE_ELEMENT_CONFIG.DRAW_MODE_STROKE_COLOR
      ),
    });
    layer.add(rectangle);
  };

  /**
   * updating rectangle size on moving mouse
   */
  const onMouseMove = () => {
    if (!startPos) return;

    const pos = stage.getPointerPosition();
    if (!pos) return;

    const width = Math.abs(pos.x - startPos.x);
    const height = Math.abs(pos.y - startPos.y);
    rectangle.width(isShiftPressed ? _.min([width, height]) : width);
    rectangle.height(isShiftPressed ? _.min([width, height]) : height);

    layer.batchDraw();
  };

  /**
   * on mouse up, add rectangle to canvas and send callback
   */
  const onMouseUp = () => {
    if (!rectangle || !startPos) return;

    const payload = {
      width: Math.max(rectangle.width(), 40),
      height: Math.max(rectangle.height(), 40),
      x: rectangle.x(),
      y: rectangle.y(),
    };

    if (_.isFunction(props?.onDrawComplete)) props.onDrawComplete(payload);

    rectangle = null;
    startPos = null;
    stage.container().style.cursor = "default";
    stage.setAttr(KONVA_ATTR.IS_DRAWING_POLYGON, false);

    if (_.isFunction(cleanClickEvent)) cleanClickEvent();
    if (_.isFunction(cleanKeyUpEvent)) cleanKeyUpEvent();
    if (_.isFunction(cleanKeyDownEvent)) cleanKeyDownEvent();
  };

  listenBackEvents(stage, transformer, {
    onMouseDown,
    onMouseMove,
    onMouseUp,
  });
};

/**
 *
 * @param {*} node
 * @param {*} konva
 * @returns is rectangle node or not
 */
export const isRectangleNode = (node, konva) => {
  return node && node instanceof konva.Rect;
};
